import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Newspaper, ExternalLink } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Press = () => {
  const [pressItems, setPressItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/press`).then(r => setPressItems(r.data)),
      axios.get(`${API}/page-content/press`).then(r => setPageContent(r.data)).catch(() => {}),
    ]).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="press-page">
      <SEO title="Press & Media - Gangadhar Som" description="Featured articles, interviews, and media coverage about artist Gangadhar Som."
        url={`${window.location.origin}/press`} />
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10 sm:mb-16">
          <span className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground">{pageContent?.page_label || 'Media Coverage'}</span>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-light tracking-tight mt-3 mb-5 sm:mb-8">
            {pageContent?.page_title || 'Press & Media'}
          </h1>
          <p className="font-sans text-sm sm:text-base md:text-lg leading-relaxed text-foreground/80 max-w-3xl">
            {pageContent?.page_description || 'Featured articles, interviews, and media coverage about the work of Gangadhar Som.'}
          </p>
        </motion.div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-64 rounded-lg" />)}
          </div>
        ) : pressItems.length === 0 ? (
          <div className="text-center py-20">
            <Newspaper size={44} className="mx-auto mb-3 text-muted-foreground" />
            <p className="font-sans text-muted-foreground">No press items found.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
            {pressItems.map((item, i) => (
              <motion.div key={item.id}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-20px" }}
                transition={{ delay: i * 0.08 }}
                className="bg-card rounded-lg overflow-hidden border border-border interactive-card group"
              >
                {item.image_url && (
                  <div className="aspect-video overflow-hidden">
                    <img src={item.image_url} alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-4 sm:p-6">
                  <div className="mb-2 sm:mb-3">
                    <span className="font-sans text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground">{item.publication}</span>
                    <span className="mx-1.5 text-muted-foreground">·</span>
                    <span className="font-sans text-[10px] sm:text-xs text-muted-foreground">{item.publication_date}</span>
                  </div>
                  <h3 className="font-serif text-lg sm:text-xl font-light mb-2 group-hover:text-accent transition-colors">{item.title}</h3>
                  {item.excerpt && <p className="font-sans text-xs sm:text-sm text-foreground/70 leading-relaxed mb-3">{item.excerpt}</p>}
                  {item.url && (
                    <a href={item.url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 font-sans text-xs sm:text-sm text-accent hover:underline">
                      Read Article <ExternalLink size={13} />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Press;
